import React, { useRef } from 'react';
import { connect } from 'react-redux';

import { LayoutSingleColumn, NamedLink, ResponsiveImage } from '../../components';
import { useConfiguration } from '../../context/configurationContext';
import { getBundledSpotlightBooks } from '../../util/bundledDiscoverSpotlight';
import { formatMoney } from '../../util/currency';
import { useIntl } from '../../util/reactIntl';
import { getSellerShopName } from '../../util/userHelpers';
import { createSlug } from '../../util/urlHelpers';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import StaticPage from '../PageBuilder/StaticPage';
import BookCover from '../TrendingPage/BookCover';

import { shopsFromListings } from './DiscoverLandingPage.duck';
import css from './DiscoverLandingPage.module.css';

const BOOKS_SHOWN = 6;
const SHOPS_SHOWN = 4;
const REQUESTS_SHOWN = 3;
const bundledSpotlightBooks = getBundledSpotlightBooks();

const SectionHeading = ({ id, title, description, link }) => (
  <header className={css.sectionHeading}>
    <div>
      <h2 className={css.sectionTitle} id={id}>
        {title}
      </h2>
      <p className={css.sectionDescription}>{description}</p>
    </div>
    {link}
  </header>
);

const SectionStatus = ({ children, error = false }) => (
  <p className={error ? css.errorStatus : css.status} role={error ? 'alert' : 'status'}>
    {children}
  </p>
);

/**
 * The PageHop editorial feature ("The Shelf Edit") shown at the top of Discover.
 *
 * @param {Object} props
 * @param {Object} props.editorial monthly editorial content
 */
const ShelfEditFeature = ({ editorial }) => {
  const intl = useIntl();
  const feature = editorial?.heroFeatures?.[0];
  if (!feature) return null;

  const spotlight = editorial.authorSpotlight;
  const hasCanadianShelf = editorial.canadianShelf?.length > 0;

  return (
    <section className={css.editFeature} aria-labelledby="discover-edit-title">
      <div className={css.editCopy}>
        <h2 className={css.editTitle} id="discover-edit-title">
          {feature.title}
        </h2>
        <p className={css.editDescription}>{feature.description}</p>
        <NamedLink
          className={css.editCta}
          name="TrendingSubpage"
          params={{ page: 'curated-reads' }}
        >
          {intl.formatMessage({ id: 'DiscoverLandingPage.editCta' })}
        </NamedLink>
        {spotlight || hasCanadianShelf ? (
          <div className={css.editAlso}>
            <ul className={css.editAlsoList}>
              {spotlight ? (
                <li>
                  <NamedLink name="TrendingSubpage" params={{ page: 'author-spotlight' }}>
                    {intl.formatMessage(
                      { id: 'DiscoverLandingPage.editSpotlight' },
                      { author: spotlight.author }
                    )}
                  </NamedLink>
                </li>
              ) : null}
              {hasCanadianShelf ? (
                <li>
                  <NamedLink
                    name="TrendingSubpage"
                    params={{ page: 'curated-reads' }}
                    to={{ hash: '#closer-to-home-title' }}
                  >
                    {intl.formatMessage({ id: 'DiscoverLandingPage.editCloserToHome' })}
                  </NamedLink>
                </li>
              ) : null}
            </ul>
          </div>
        ) : null}
      </div>
      <ul className={css.editCovers}>
        {(feature.books || []).slice(0, 4).map(book => (
          <li key={book.title} className={css.editCover}>
            <div className={css.editCoverImage}>
              <BookCover
                title={book.title}
                author={book.author}
                coverUrl={book.cover?.resolvedUrl}
              />
            </div>
            <p className={css.editCoverCaption}>
              <strong>{book.title}</strong>
              <span>{book.supportingLine}</span>
            </p>
          </li>
        ))}
      </ul>
    </section>
  );
};

/** Editorial search picks; no inventory availability or popularity metrics are inferred. */
const BackInSpotlight = ({ books = [] }) => {
  const intl = useIntl();
  const rail = useRef(null);
  if (!books.length) return null;
  const scroll = direction =>
    rail.current?.scrollBy({
      left: direction * rail.current.clientWidth,
      behavior: 'smooth',
    });
  return (
    <section className={css.section} aria-labelledby="discover-spotlight-title">
      <SectionHeading
        id="discover-spotlight-title"
        title={intl.formatMessage({ id: 'DiscoverLandingPage.spotlightTitle' })}
        description={intl.formatMessage({ id: 'DiscoverLandingPage.spotlightDescription' })}
        link={
          <div className={css.spotlightControls}>
            <button
              type="button"
              className={css.spotlightControl}
              onClick={() => scroll(-1)}
              aria-controls="discover-spotlight-books"
              aria-label={intl.formatMessage({ id: 'DiscoverLandingPage.spotlightPrevious' })}
            >
              <span aria-hidden="true">&larr;</span>
            </button>
            <button
              type="button"
              className={css.spotlightControl}
              onClick={() => scroll(1)}
              aria-controls="discover-spotlight-books"
              aria-label={intl.formatMessage({ id: 'DiscoverLandingPage.spotlightNext' })}
            >
              <span aria-hidden="true">&rarr;</span>
            </button>
          </div>
        }
      />
      <ul
        className={css.spotlightRail}
        id="discover-spotlight-books"
        ref={rail}
        tabIndex={0}
        aria-labelledby="discover-spotlight-title"
      >
        {books.map(book => (
          <li className={css.spotlightCard} key={`${book.title}|${book.author}`}>
            <NamedLink
              className={css.spotlightLink}
              name="SearchPage"
              to={{ search: `?keywords=${encodeURIComponent(book.title)}` }}
              ariaLabel={intl.formatMessage(
                { id: 'DiscoverLandingPage.spotlightSearch' },
                { title: book.title, author: book.author }
              )}
            >
              <div className={css.spotlightCover} aria-hidden="true">
                <BookCover title={book.title} author={book.author} coverUrl={book.coverUrl} />
              </div>
              <p className={css.spotlightCategory}>
                {book.reasonLabel
                  ? intl.formatMessage({
                      id: `DiscoverLandingPage.spotlight.${book.editorialKey || 'custom'}.reason`,
                      defaultMessage: book.reasonLabel,
                    })
                  : intl.formatMessage({
                      id: `DiscoverLandingPage.spotlight.${book.category}`,
                    })}
              </p>
              <h3 className={css.spotlightBookTitle}>{book.title}</h3>
              <p className={css.bookAuthor}>{book.author}</p>
              {book.note ? (
                <p className={css.spotlightNote}>
                  {intl.formatMessage({
                    id: `DiscoverLandingPage.spotlight.${book.editorialKey || 'custom'}.note`,
                    defaultMessage: book.note,
                  })}
                </p>
              ) : null}
            </NamedLink>
          </li>
        ))}
      </ul>
    </section>
  );
};

const BookCard = ({ listing }) => {
  const config = useConfiguration();
  const intl = useIntl();
  const attributes = listing.attributes || {};
  const publicData = attributes.publicData || {};
  const title = attributes.title || '';
  const image = listing.images?.[0];
  const { variantPrefix = 'listing-card' } = config.layout?.listingImage || {};
  const variants = image
    ? Object.keys(image.attributes?.variants || {}).filter(name => name.startsWith(variantPrefix))
    : [];
  const author = publicData.author || publicData.bookAuthor;
  const seller = listing.author;
  const sellerName = seller
    ? getSellerShopName(seller, seller.attributes?.profile?.displayName)
    : null;

  return (
    <li className={css.bookCard}>
      <NamedLink
        className={css.bookImageLink}
        name="ListingPage"
        params={{ id: listing.id.uuid, slug: createSlug(title) }}
      >
        <ResponsiveImage
          rootClassName={css.bookImage}
          image={image}
          variants={variants}
          sizes="(max-width: 767px) 44vw, (max-width: 1023px) 30vw, 180px"
          alt={author ? `Cover of ${title} by ${author}` : `Cover of ${title}`}
        />
      </NamedLink>
      <NamedLink
        className={css.bookTitle}
        name="ListingPage"
        params={{ id: listing.id.uuid, slug: createSlug(title) }}
      >
        {title}
      </NamedLink>
      {author ? <p className={css.bookAuthor}>{author}</p> : null}
      <p className={css.bookMeta}>
        {attributes.price ? (
          <span className={css.bookPrice}>{formatMoney(intl, attributes.price)}</span>
        ) : null}
        {sellerName ? (
          <NamedLink className={css.sellerLink} name="ShopPage" params={{ id: seller.id.uuid }}>
            {sellerName}
          </NamedLink>
        ) : null}
      </p>
    </li>
  );
};

const ShopRow = ({ seller }) => {
  const intl = useIntl();
  const profile = seller.attributes?.profile || {};
  const shopName = getSellerShopName(seller, profile.displayName);
  const province = profile.publicData?.province;
  const image = seller.profileImage;
  const variants = ['square-small', 'square-small2x', 'square-xsmall', 'square-xsmall2x'];
  const monogram = (shopName || '?')
    .trim()
    .charAt(0)
    .toUpperCase();

  return (
    <li className={css.shopRow}>
      <NamedLink
        className={css.shopPortraitLink}
        name="ShopPage"
        params={{ id: seller.id.uuid }}
        ariaLabel={intl.formatMessage({ id: 'DiscoverLandingPage.visitShopAria' }, { shopName })}
      >
        {image ? (
          <ResponsiveImage
            rootClassName={css.shopPortrait}
            image={image}
            variants={variants}
            sizes="72px"
            alt=""
          />
        ) : (
          <span className={css.shopMonogram} aria-hidden="true">
            {monogram}
          </span>
        )}
      </NamedLink>
      <div className={css.shopDetails}>
        <h3 className={css.shopTitle}>
          <NamedLink className={css.shopNameLink} name="ShopPage" params={{ id: seller.id.uuid }}>
            {shopName}
          </NamedLink>
        </h3>
        {province ? (
          <p className={css.shopMeta}>
            <span>{province}</span>
          </p>
        ) : null}
      </div>
      <NamedLink
        className={css.shopVisit}
        name="ShopPage"
        params={{ id: seller.id.uuid }}
        ariaLabel={intl.formatMessage({ id: 'DiscoverLandingPage.visitShopAria' }, { shopName })}
      >
        {intl.formatMessage({ id: 'DiscoverLandingPage.visitShop' })}
      </NamedLink>
    </li>
  );
};

const RequestRow = ({ listing }) => {
  const intl = useIntl();
  const attributes = listing.attributes || {};
  const publicData = attributes.publicData || {};
  const title = attributes.title || '';
  const author = publicData.author || publicData.bookAuthor;
  const budget = publicData.budget || publicData.maxBudget;

  return (
    <li className={css.requestRow}>
      <NamedLink
        className={css.requestTitle}
        name="ListingPage"
        params={{ id: listing.id.uuid, slug: createSlug(title) }}
      >
        {title}
      </NamedLink>
      {author ? <p className={css.requestAuthor}>{author}</p> : null}
      {budget ? (
        <p className={css.requestBudget}>
          {intl.formatMessage({ id: 'DiscoverLandingPage.isoBudget' }, { budget })}
        </p>
      ) : null}
    </li>
  );
};

/**
 * The canonical PageHop Discover page, served at /trending.
 *
 * Combines the PageHop editorial feature with marketplace-backed sections. Each marketplace
 * section handles its own loading, error, and empty state so one failure does not hide the rest.
 * "Meet the shops" follows recent listing order, not sales performance. Top Sellers requires a
 * complete server-side sales aggregate and current seller eligibility; public reviews, manual
 * badges, private sold history, and launch-gated rewards are not substitutes for that feed.
 *
 * @component
 * @param {Object} props
 * @param {Array<Object>} props.books newest published book listings
 * @param {Array<Object>} props.isoRequests newest published ISO requests
 * @param {boolean} props.booksLoading
 * @param {boolean} props.isoLoading
 * @param {Object?} props.booksError
 * @param {Object?} props.isoError
 * @param {Object?} props.loadError
 * @param {Object?} props.editorial editorial content from the hosted Discover page
 * @param {Array<Object>} props.spotlightBooks deployment-managed editorial search picks
 * @returns {JSX.Element}
 */
export const DiscoverLandingPageComponent = ({
  spotlightBooks = bundledSpotlightBooks,
  books = [],
  isoRequests = [],
  booksLoading = false,
  isoLoading = false,
  booksError,
  isoError,
  loadError,
  editorial = null,
}) => {
  const intl = useIntl();
  const shops = shopsFromListings(books);
  const showShops = !booksLoading && !booksError && shops.length > 0;
  const postIsoLink = {
    pathname: '/l/new',
    search: '?listingType=iso-book',
  };

  return (
    <StaticPage
      title={intl.formatMessage({ id: 'DiscoverLandingPage.pageTitle' })}
      description={intl.formatMessage({ id: 'DiscoverLandingPage.intro' })}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <main className={css.root} id="main-content">
          <header className={css.intro}>
            <h1 className={css.introTitle}>
              {intl.formatMessage({ id: 'DiscoverLandingPage.title' })}
            </h1>
            <p className={css.introText}>
              {intl.formatMessage({ id: 'DiscoverLandingPage.intro' })}
            </p>
          </header>
          {loadError ? (
            <p className={css.errorStatus} role="alert">
              {intl.formatMessage({ id: 'DiscoverLandingPage.loadError' })}
            </p>
          ) : null}

          <ShelfEditFeature editorial={editorial} />

          <section className={css.section} aria-labelledby="discover-books-title">
            <SectionHeading
              id="discover-books-title"
              title={intl.formatMessage({ id: 'DiscoverLandingPage.booksTitle' })}
              description={intl.formatMessage({ id: 'DiscoverLandingPage.booksDescription' })}
              link={
                <NamedLink className={css.sectionLink} name="SearchPage">
                  {intl.formatMessage({ id: 'DiscoverLandingPage.browseBooks' })}
                </NamedLink>
              }
            />
            {booksLoading ? (
              <SectionStatus>
                {intl.formatMessage({ id: 'DiscoverLandingPage.loadingBooks' })}
              </SectionStatus>
            ) : booksError ? (
              <SectionStatus error>
                {intl.formatMessage({ id: 'DiscoverLandingPage.booksError' })}
              </SectionStatus>
            ) : books.length ? (
              <ul className={css.bookGrid}>
                {books.slice(0, BOOKS_SHOWN).map(listing => (
                  <BookCard key={listing.id.uuid} listing={listing} />
                ))}
              </ul>
            ) : (
              <SectionStatus>
                {intl.formatMessage({ id: 'DiscoverLandingPage.noBooks' })}
              </SectionStatus>
            )}
          </section>

          <BackInSpotlight books={spotlightBooks} />

          <div className={showShops ? css.split : css.splitSingle}>
            {showShops ? (
              <section className={css.splitColumn} aria-labelledby="discover-shops-title">
                <SectionHeading
                  id="discover-shops-title"
                  title={intl.formatMessage({ id: 'DiscoverLandingPage.shopsTitle' })}
                  description={intl.formatMessage({
                    id: 'DiscoverLandingPage.shopsDescription',
                  })}
                  link={
                    <NamedLink className={css.sectionLink} name="ShopsPage">
                      {intl.formatMessage({ id: 'DiscoverLandingPage.browseShops' })}
                    </NamedLink>
                  }
                />
                <ul className={css.shopList}>
                  {shops.slice(0, SHOPS_SHOWN).map(seller => (
                    <ShopRow key={seller.id.uuid} seller={seller} />
                  ))}
                </ul>
              </section>
            ) : null}

            <section
              className={`${css.splitColumn} ${css.requests}`}
              aria-labelledby="discover-iso-title"
            >
              <SectionHeading
                id="discover-iso-title"
                title={intl.formatMessage({ id: 'DiscoverLandingPage.isoTitle' })}
                description={intl.formatMessage({ id: 'DiscoverLandingPage.isoDescription' })}
                link={
                  <NamedLink
                    className={css.sectionLink}
                    name="SearchPageWithListingType"
                    params={{ listingType: 'iso-book' }}
                  >
                    {intl.formatMessage({ id: 'DiscoverLandingPage.allRequests' })}
                  </NamedLink>
                }
              />
              {isoLoading ? (
                <SectionStatus>
                  {intl.formatMessage({ id: 'DiscoverLandingPage.loadingISO' })}
                </SectionStatus>
              ) : isoError ? (
                <SectionStatus error>
                  {intl.formatMessage({ id: 'DiscoverLandingPage.isoError' })}
                </SectionStatus>
              ) : isoRequests.length ? (
                <ul className={css.requestList}>
                  {isoRequests.slice(0, REQUESTS_SHOWN).map(listing => (
                    <RequestRow key={listing.id.uuid} listing={listing} />
                  ))}
                </ul>
              ) : (
                <SectionStatus>
                  {intl.formatMessage({ id: 'DiscoverLandingPage.noISO' })}
                </SectionStatus>
              )}
              <NamedLink
                className={css.postIsoLink}
                name="EditListingPage"
                params={{
                  slug: 'draft',
                  id: '00000000-0000-0000-0000-000000000000',
                  type: 'new',
                  tab: 'details',
                }}
                to={postIsoLink}
              >
                {intl.formatMessage({ id: 'DiscoverLandingPage.postISO' })}
              </NamedLink>
            </section>
          </div>
        </main>
      </LayoutSingleColumn>
    </StaticPage>
  );
};

const mapStateToProps = state => ({
  books: state.DiscoverLandingPage?.books || [],
  isoRequests: state.DiscoverLandingPage?.isoRequests || [],
  booksLoading: state.DiscoverLandingPage?.booksLoading || false,
  isoLoading: state.DiscoverLandingPage?.isoLoading || false,
  booksError: state.DiscoverLandingPage?.booksError,
  isoError: state.DiscoverLandingPage?.isoError,
  loadError: state.DiscoverLandingPage?.loadError,
  editorial: state.DiscoverEditorial?.content || null,
});

const DiscoverLandingPage = connect(mapStateToProps)(DiscoverLandingPageComponent);

export { DiscoverLandingPage };
export default DiscoverLandingPage;
